import { RefObject, useEffect, useRef } from "react";
import styled from "styled-components";

import image01 from "../../assets/hero-image/01.webp";
import image02 from "../../assets/hero-image/02.svg";
import image03 from "../../assets/hero-image/03.webp";
import image04 from "../../assets/hero-image/04.webp";

const MOBILE_MAX_WIDTH = 1000;

// Preload the LCP overlay layer as soon as the chunk evaluates — earlier than
// any render, and immune to styled-components' attribute filtering (which
// strips fetchpriority from the <img> itself).
if (typeof document !== "undefined") {
  const preload = document.createElement("link");
  preload.rel = "preload";
  preload.as = "image";
  preload.href = image04;
  preload.setAttribute("fetchpriority", "high");
  document.head.appendChild(preload);
}

const StyledHeroImage = styled.div`
  position: relative;
  width: 100%;
  overflow: visible;
  max-width: 130rem;
  margin-bottom: 6rem;
  cursor: crosshair;
  perspective: 1000px;
  /* Reserve the overlay's intrinsic 2600x1726 box so nothing below shifts
     while the hero layers download (CLS). */
  aspect-ratio: 2600 / 1726;

  @media only screen and (max-width: ${MOBILE_MAX_WIDTH}px) {
    max-width: min(100%, 44rem);
    padding: 0 clamp(1.2rem, 4vw, 2.4rem);
    box-sizing: border-box;
    cursor: default;
    perspective: none;
  }

  @media only screen and (max-width: 600px) {
    max-width: none;
    width: 100vw;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    padding: 0;
    margin-bottom: 0.6rem;
    margin-top: 0;
  }

  @media only screen and (min-width: 601px) and (max-width: 1200px) {
    margin-bottom: 4rem;
    margin-top: 0;
  }
`;

const InnerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.2s ease-out;
  transform-style: preserve-3d;
  overflow: hidden;
  will-change: transform;

  @media only screen and (max-width: ${MOBILE_MAX_WIDTH}px) {
    transition: none;
  }
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  /* the width/height attributes exist for the intrinsic aspect ratio only;
     without this, the attribute height wins as a presentational hint */
  height: auto;
  pointer-events: none;
  transition: transform 0.1s ease-out;
  will-change: transform;

  @media only screen and (max-width: ${MOBILE_MAX_WIDTH}px) {
    transition: none;
  }
`;

const Overlay = styled.img`
  position: relative;
  width: 100%;
  height: auto;
  pointer-events: none;
  z-index: 10;

  @media only screen and (max-width: ${MOBILE_MAX_WIDTH}px) {
    will-change: transform;
  }
`;

const Gradient = styled.div`
  position: absolute;
  z-index: 15;
  pointer-events: none;
`;

const GradientLeft = styled(Gradient)`
  top: 0;
  left: 0;
  height: 100%;
  width: 10%;
  background: linear-gradient(to right, var(--bg), transparent);

  @media only screen and (max-width: 600px) {
    display: none;
  }
`;

const GradientRight = styled(Gradient)`
  top: 0;
  right: 0;
  height: 100%;
  width: 10%;
  background: linear-gradient(to left, var(--bg), transparent);

  @media only screen and (max-width: 600px) {
    display: none;
  }
`;

const GradientTop = styled(Gradient)`
  top: 0;
  left: 0;
  width: 100%;
  height: 20%;
  background: linear-gradient(to bottom, var(--bg), transparent);

  @media only screen and (max-width: 600px) {
    height: 32%;
    background: linear-gradient(
      to bottom,
      var(--bg) 0%,
      rgba(249, 247, 247, 0.88) 22%,
      rgba(249, 247, 247, 0.35) 58%,
      transparent 100%
    );
  }
`;

const GradientBottom = styled(Gradient)`
  bottom: 0;
  left: 0;
  width: 100%;
  height: 20%;
  background: linear-gradient(to top, var(--bg), transparent);

  @media only screen and (max-width: 600px) {
    height: 34%;
    background: linear-gradient(
      to top,
      var(--bg) 0%,
      rgba(249, 247, 247, 0.9) 20%,
      rgba(249, 247, 247, 0.4) 55%,
      transparent 100%
    );
  }
`;

const isMobileViewport = () =>
  window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;

const getScrollElement = (scrollContainerRef?: RefObject<HTMLElement>) => {
  const root = scrollContainerRef?.current;
  if (!root) return window;

  const { overflowY } = window.getComputedStyle(root);
  if (overflowY === "visible" || overflowY === "hidden") {
    return window;
  }

  return root.scrollHeight > root.clientHeight ? root : window;
};

interface HeroImageProps {
  scrollContainerRef?: RefObject<HTMLElement>;
}

const HeroImage = ({ scrollContainerRef }: HeroImageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLImageElement>(null);
  const layerOneRef = useRef<HTMLImageElement>(null);
  const layerTwoRef = useRef<HTMLImageElement>(null);
  const layerThreeRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const inner = innerRef.current;
    const overlay = overlayRef.current;
    const layerOne = layerOneRef.current;
    const layerTwo = layerTwoRef.current;
    const layerThree = layerThreeRef.current;

    if (!container || !inner || !layerOne || !layerTwo || !layerThree) return;

    let frame = 0;
    let mouseX = 0;
    let mouseY = 0;
    let mobileMode = isMobileViewport();

    const resetTransforms = () => {
      inner.style.transform = "";
      if (overlay) overlay.style.transform = "";
      layerOne.style.transform = "";
      layerTwo.style.transform = "";
      layerThree.style.transform = "";
    };

    const applyDesktopParallax = () => {
      const tiltX = mouseY * -2;
      const tiltY = mouseX * 2;

      inner.style.transform = `rotateX(${tiltX}deg) rotateY(${tiltY}deg)`;
      layerOne.style.transform = `translate(${mouseX * 5}px, ${mouseY * 5}px) scale(1.05)`;
      layerTwo.style.transform = `translate(${mouseX * 15}px, ${mouseY * 15}px) scale(0.9)`;
      layerThree.style.transform = `translate(${mouseX * 30}px, ${mouseY * 30}px)`;
    };

    const applyMobileScrollParallax = () => {
      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const travel = viewportHeight + rect.height;
      const progress = Math.min(1, Math.max(0, (viewportHeight - rect.top) / travel));
      const offset = (progress - 0.35) * 2;

      inner.style.transform = `translate3d(0, ${offset * -10}px, 0)`;
      if (overlay) {
        overlay.style.transform = `translate3d(0, ${offset * -6}px, 0)`;
      }
      layerOne.style.transform = `translate3d(0, ${offset * 14}px, 0) scale(1.05)`;
      layerTwo.style.transform = `translate3d(0, ${offset * 26}px, 0) scale(0.94)`;
      layerThree.style.transform = `translate3d(0, ${offset * 40}px, 0)`;
    };

    const scheduleDesktopApply = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        if (mobileMode) return;
        applyDesktopParallax();
      });
    };

    const scheduleMobileApply = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        if (!mobileMode) return;
        applyMobileScrollParallax();
      });
    };

    const handleMove = (event: MouseEvent) => {
      if (mobileMode) return;

      const rect = container.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      mouseX = Math.max(
        -1.5,
        Math.min(1.5, (event.clientX - centerX) / (rect.width / 2))
      );
      mouseY = Math.max(
        -1.5,
        Math.min(1.5, (event.clientY - centerY) / (rect.height / 2))
      );
      scheduleDesktopApply();
    };

    const handleLeave = () => {
      if (mobileMode) return;
      mouseX = 0;
      mouseY = 0;
      scheduleDesktopApply();
    };

    const syncMode = () => {
      const nextMobileMode = isMobileViewport();
      if (nextMobileMode === mobileMode) {
        if (mobileMode) scheduleMobileApply();
        else scheduleDesktopApply();
        return;
      }

      mobileMode = nextMobileMode;
      resetTransforms();

      if (mobileMode) {
        mouseX = 0;
        mouseY = 0;
        scheduleMobileApply();
      } else {
        scheduleDesktopApply();
      }
    };

    const mediaQuery = window.matchMedia(
      `(max-width: ${MOBILE_MAX_WIDTH}px)`
    );

    // The scrolling element differs across the breakpoint (inner snap
    // container on desktop, window on mobile), so re-resolve it whenever
    // the mode changes instead of binding once at mount.
    let scrollElement: HTMLElement | Window = getScrollElement(
      scrollContainerRef
    );
    const rebindScroll = () => {
      scrollElement.removeEventListener("scroll", scheduleMobileApply);
      scrollElement = getScrollElement(scrollContainerRef);
      scrollElement.addEventListener("scroll", scheduleMobileApply, {
        passive: true,
      });
    };

    const handleModeChange = () => {
      rebindScroll();
      syncMode();
    };

    container.addEventListener("mousemove", handleMove);
    container.addEventListener("mouseleave", handleLeave);
    scrollElement.addEventListener("scroll", scheduleMobileApply, {
      passive: true,
    });
    window.addEventListener("resize", syncMode);
    mediaQuery.addEventListener("change", handleModeChange);

    syncMode();

    return () => {
      container.removeEventListener("mousemove", handleMove);
      container.removeEventListener("mouseleave", handleLeave);
      scrollElement.removeEventListener("scroll", scheduleMobileApply);
      window.removeEventListener("resize", syncMode);
      mediaQuery.removeEventListener("change", handleModeChange);
      if (frame) window.cancelAnimationFrame(frame);
      resetTransforms();
    };
  }, [scrollContainerRef]);

  return (
    <StyledHeroImage ref={containerRef}>
      <InnerContainer ref={innerRef}>
        <Image
          ref={layerOneRef}
          src={image01}
          alt=""
          width={2600}
          height={5662}
          style={{ zIndex: 1 }}
        />
        <Image
          ref={layerTwoRef}
          src={image02}
          alt=""
          style={{ zIndex: 2 }}
        />
        <Image
          ref={layerThreeRef}
          src={image03}
          alt=""
          width={2600}
          height={2593}
          style={{ zIndex: 3 }}
        />
        <Overlay
          ref={overlayRef}
          src={image04}
          alt="Killer GF assassins artwork"
          width={2600}
          height={1726}
        />
        <GradientLeft />
        <GradientRight />
        <GradientTop />
        <GradientBottom />
      </InnerContainer>
    </StyledHeroImage>
  );
};

export default HeroImage;
